import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import '../../../assets/css/player.css';
import '../css/creator/feed.css';
import {
  formatTimeAgo,
  formatDuration,
  isBundleContent,
  getMediaArray,
  getPrimaryMedia
} from '../../../shared/utils/function';
import {
  DEFAULT_USER_AVATAR,
  MEDIA_TYPE,
  PLACEHOLDER_POST_IMAGE,
  ROLE,
  MESSAGE_READ
} from '../../../shared/constants/common';
import { useNavigation } from '../../../shared/utils/useNavigation';
import BundleSlider from "../../../features/slider/BundleSlider";
import LazyImage from '../../../components/LazyImage/LazyImage';
import MediaGridDropdown from './MediaGridDropdown';
import VideoPlayer from '../../../shared/components/VideoPlayer';

const FeedPost = ({
  postId = null,
  post,
  isLogging = false,
  userRole = null,
  isCreatorOwnProfile = false,
  isPinAlreadyExists = false,
  creatorInfo = null,
  // coming from mapStateToProps
  user = null,
  // State management for dropdown
  openDropdownId = null,
  onDropdownToggle = null,
  // bubble delete event to parent list (optional)
  onPostDeleted = null,
  // bubble update event to parent (optional)
  onPostUpdated = null,
  isLock = false,
  isShowDuration = false,
  showControls = false,
}) => {
  const navigate = useNavigation();
  const real_price = parseFloat(post?.real_price || 0).toFixed(2);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoPlaybackStarted, setVideoPlaybackStarted] = useState(false);
  const [bundleSlideIndex, setBundleSlideIndex] = useState(0);

  const likes = post.tips || 0;
  const isBundle = isBundleContent(post);
  const mediaArray = getMediaArray(post);
  const primaryMedia = getPrimaryMedia(post);

  useEffect(() => {
    setIsVideoPlaying(false);
    setVideoPlaybackStarted(false);
  }, [bundleSlideIndex]);

  const handleSingleVideoPlayStarted = () => {
    setIsVideoPlaying(true);
    setVideoPlaybackStarted(true);
  };

  const handleSingleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleSingleVideoScrollPause = () => {
    setVideoPlaybackStarted(false);
    setIsVideoPlaying(false);
  };

  const activeMediaContext = isBundle
    ? (mediaArray[bundleSlideIndex] || primaryMedia)
    : primaryMedia;
  const isActiveVideo = activeMediaContext?.type_file === MEDIA_TYPE.VIDEO || activeMediaContext?.type === MEDIA_TYPE.VIDEO;
  // Show Premium/Free/price when paused; hide only while the active video is playing.
  const shouldShowVideoBadges = !isActiveVideo || !isVideoPlaying;

  const CaptionWithToggle = ({ text, maxLength = 100 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return null;

    const toggleReadMore = () => setIsExpanded(!isExpanded);

    return (
      <span className="caption-text">
        {isExpanded ? text : text.slice(0, maxLength) + (text.length > maxLength ? '...' : '')}
        {text.length > maxLength && (
          <span onClick={toggleReadMore} className="read-more-btn">
            {isExpanded ? MESSAGE_READ.SHOW_LESS_TEXT : MESSAGE_READ.SHOW_MORE_TEXT}
          </span>
        )}
      </span>
    );
  };

  const handleTagUser = (user) => {
    if (isLogging && isCreatorOwnProfile) {
      if(user?._id === creatorInfo?._id) {
        navigate.toMessages();
      } else {
        navigate.toChat(user?._id);
      } 
    } else {
      if (user.username) {
        window.location.href = `/${user.username}`;
      }
    }
  };

  return (
    <div className="modern-post-ui cm_modern_post_card" data-post-id={postId}>
      <div className="cm_modern_post_content">

        <div className='cm_pin_time_bage'>
          {post.pin && (
            <span className="cm_pin_badge">
              <i className='fas fa-thumbtack pinned-icon' title="Pinned" aria-label="Pinned"></i>
            </span>
          )}

          <span className="cm_timestamp_badge">
            {formatTimeAgo(post.created_at)}
          </span>
        </div>

        <div className="post_user_info">
          <LazyImage
            src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
            alt={creatorInfo?.username}
            className='cm-post-height'
            onError={(e) => {
              e.target.src = DEFAULT_USER_AVATAR;
            }}
          />
        </div>

        <div className='cm_modern_post_text'>
          <div className="username-section">
            <h6>@{creatorInfo?.username}</h6>
          </div>
          {post.caption && (
            <div className="cm_post_caption">
              <CaptionWithToggle
                text={post.caption}
                maxLength={100}
              />
            </div>
          )}

          {post.tag_users && post.tag_users.length > 0 && (
            <div className="tagged-users-section">
              {post.tag_users.map((user, index) => (
                <span key={index} className="tag-user" onClick={() => handleTagUser(user)}>
                  @{user.username || user}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="post-content">
        <div className="post-media-container">
          {shouldShowVideoBadges && (
            <>
              <div className={`content-type-badge ${post.lock_content ? 'premium' : 'free'}`}>
                {post.lock_content ? 'Premium' : 'Free'}
              </div>

              {post.lock_content && real_price > 0 && (
                <div className="price-badge">
                  <i className='fa-solid fa-lock'></i>
                  ${real_price}
                </div>
              )}
            </>
          )}

          {isBundle && mediaArray && mediaArray.length > 0 ? (
            <BundleSlider
              media={mediaArray}
              showControls={showControls}
              isLock={isLock}
              isShowDuration={isShowDuration}
              onMessagingVideoPlayingChange={setIsVideoPlaying}
              onVideoPlaybackStartedChange={setVideoPlaybackStarted}
              onCurrentIndexChange={setBundleSlideIndex}
            />
          ) : primaryMedia.type === MEDIA_TYPE.IMAGE ? (
            <LazyImage
              src={primaryMedia.src}
              alt="Post content"
              className='cm-post-height post-media'
              onError={(e) => {
                e.target.src = PLACEHOLDER_POST_IMAGE;
              }}
            />
          ) : (
            <VideoPlayer
              src={primaryMedia.src}
              poster={primaryMedia.poster || null}
              className='cm-post-height video-no-timeline bundle-slider-media'
              showControls={showControls}
              controlsList="nodownload noplaybackrate noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              preventContextMenu
              muted
              loop
              playsInline
              fallbackText='Video unavailable'
              showDurationBadge
              playbackStarted={videoPlaybackStarted}
              onPlayStarted={handleSingleVideoPlayStarted}
              onPause={handleSingleVideoPause}
              onScrollPause={handleSingleVideoScrollPause}
              pauseWhenOutOfView
            />
          )}

          {!isBundle &&
            isShowDuration &&
            (post.type_file === MEDIA_TYPE.VIDEO || post.type === MEDIA_TYPE.VIDEO) &&
            post.duration &&
            !isVideoPlaying &&
            !videoPlaybackStarted && (
            <div className={`video_time_badge`}>
              <i className='fa-solid fa-video'></i>
              {formatDuration(post.duration)}
            </div>
          )}
        </div>
      </div>

      <div className="post-actions">
        <div className="actions-left">
          <div className="actions-group">
            <div className="like-section">
              <div className="like-icon">
                <i className='fas fa-heart'></i>
              </div>
              <span className="like-count">
                {likes.toLocaleString()}
              </span>
            </div>

            <div
              className="tips-display"
            >
              <div className="tips-icon">
                <i className="fa-solid fa-sack-dollar tips-icon-text"></i>
              </div>
              <span className="tips-amount color_green">
                $ {post.total_amount ? parseFloat(post.total_amount).toFixed(2) : 0}
              </span>
            </div>

          </div>


          {userRole === ROLE.CREATOR_CONTENT && post?.isTrending && post?.isTrending === true && (
            <div className="trending-badge">
              <span className="trending-badge-text">
                Trending
              </span>
              <i className='fas fa-fire'></i>
            </div>
          )}
        </div>



        {/* Edit Menu - positioned at top-right of entire post */}
        <MediaGridDropdown
          className=""
          iconClass="fa-pen-to-square"
          post={post}
          userRole={userRole}
          isPinAlreadyExists={isPinAlreadyExists}
          isShowPin={true}
          isOpen={openDropdownId === (post._id || post.id)}
          onToggle={onDropdownToggle}
          onDeleted={() => onPostDeleted?.(post._id || post.id)}
          onUpdated={(action, postId, newValue) => onPostUpdated?.(action, postId, newValue)}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(FeedPost);
